import { useCallback, useEffect, useState } from "react";
import axios from "axios";

export const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const HTTP_UNAUTHORIZED = 401;
export const HTTP_CONFLICT = 409;
// Session lives in an httpOnly cookie set by the backend: no token is ever stored in browser storage
export const api = axios.create({ baseURL: `${BACKEND}/api`, withCredentials: true });

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const url = err.config?.url || "";
    const silent = url.includes("/auth/login") || url.includes("/auth/me") || url.includes("/auth/logout");
    if (err.response?.status === HTTP_UNAUTHORIZED && !silent && window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

export function errMsg(e, fallback = "Si è verificato un errore") {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  if (d.message) return d.message;
  return fallback;
}

export function fileUrl(p) {
  if (!p) return null;
  return p.startsWith("/api") ? `${BACKEND}${p}` : p;
}

const INLINE_TYPES = ["application/pdf", "image/png", "image/jpeg", "image/webp", "image/gif"];

function dispositionName(header) {
  const m = /filename\*=UTF-8''([^;]+)/i.exec(header || "") || /filename="([^"]+)"/i.exec(header || "");
  return m ? decodeURIComponent(m[1]) : "file";
}

export async function openBlob(path, filename) {
  const res = await api.get(path, { responseType: "blob" });
  const inline = INLINE_TYPES.includes(res.data.type);
  // Only PDFs and images open in a tab: any other file is downloaded so it can never run in the app
  const blob = inline ? res.data : new Blob([res.data], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  if (filename || !inline) {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || dispositionName(res.headers["content-disposition"]);
    a.click();
  } else {
    window.open(url, "_blank");
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export function useApi(url, params) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const key = JSON.stringify(params || {});
  const load = useCallback(async () => {
    if (!url) return;
    setLoading(true);
    try {
      const r = await api.get(url, { params: JSON.parse(key) });
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [url, key]);
  useEffect(() => {
    load();
  }, [load]);
  return { data, loading, error, reload: load, setData };
}
